<section
  class="workflow-graph-view"
  [class.workflow-graph-view--contextual]="contextualInspector"
  data-testid="workflow-read-only-graph"
>
  <ng-container *ngIf="graph as currentGraph">
    <p
      *ngIf="currentGraph.nodes.length === 0"
      class="workflow-graph-view__empty"
      role="status"
      aria-live="polite"
      data-testid="workflow-graph-empty"
    >
      {{ sourceEmptyMessage() }}
    </p>

    <ng-container *ngIf="currentGraph.nodes.length > 0">
      <p
        *ngIf="isBusy()"
        class="workflow-graph-view__status"
        role="status"
        aria-live="polite"
        data-testid="workflow-graph-rendering"
      >
        <ng-container *ngIf="isWaitingForViewport(); else renderingStatus">
          {{ sourcePreparingMessage() }}
        </ng-container>
        <ng-template #renderingStatus>
          <ng-container *ngIf="hasRenderedVisual(); else initialRender">
            {{ sourceUpdatingMessage() }}
          </ng-container>
        </ng-template>
        <ng-template #initialRender>{{ sourceRenderingMessage() }}</ng-template>
      </p>

      <div
        *ngIf="renderErrorMessage() as errorMessage"
        #renderError
        class="workflow-graph-view__error"
        role="alert"
        aria-live="assertive"
        tabindex="-1"
        data-testid="workflow-graph-render-error"
      >
        <strong>Graph rendering failed</strong>
        <p>{{ errorMessage }}</p>
        <p *ngIf="hasRenderedVisual()">
          The retained visual represents an earlier applied document and is
          stale.
        </p>
        <button
          uiButton
          type="button"
          [disabled]="isRendering()"
          (click)="retry()"
        >
          Retry graph rendering
        </button>
      </div>

      <p
        *ngIf="contextualInspector"
        class="visually-hidden"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        data-testid="workflow-graph-selection-announcement"
      >
        {{ selectionAnnouncement() }}
      </p>

      <div
        class="workflow-graph-view__layout"
        [class.workflow-graph-view__layout--contextual]="contextualInspector"
        [class.workflow-graph-view__layout--standalone]="contextualInspector && inspectorStandalone"
        [class.workflow-graph-view__layout--finder-open]="
          contextualInspector && elementFinderOpen()
        "
        [class.workflow-graph-view__layout--inspector-collapsed]="contextualInspector && !inspectorStandalone && inspectorCollapsed()"
        [style.--workflow-inspector-width.px]="inspectorWidth()"
      >
        <span *ngIf="canArrangeNodes" class="visually-hidden" role="status" aria-live="polite">{{layoutMessage()}}</span>
        <section
          id="workflow-graph-element-finder"
          class="workflow-graph-view__picker"
          aria-label="Workflow elements"
          [hidden]="contextualInspector && !elementFinderOpen()"
          (keydown)="onElementFinderKeydown($event)"
        >
          <header class="workflow-graph-view__picker-header">
            <div>
              <h5>Elements</h5>
              <span
                role="status"
                aria-live="polite"
                data-testid="workflow-graph-filter-result"
              >
                {{ filterResultLabel() }}
              </span>
            </div>

            <button
              uiButton
              [text]="true"
              [outlined]="false"
              *ngIf="contextualInspector"
              type="button"
              class="workflow-graph-view__finder-close"
              (click)="closeElementFinder()"
            >
              Close
            </button>

            <div class="workflow-graph-view__filter-row">
              <label class="workflow-graph-view__filter">
                <span>Find elements</span>
                <span class="workflow-graph-view__filter-control">
                  <i class="pi pi-search" aria-hidden="true"></i>
                  <input
                    uiControl
                    #elementFilterInput
                    type="search"
                    autocomplete="off"
                    [value]="elementFilter()"
                    (input)="setElementFilter($event)"
                    data-testid="workflow-graph-element-filter"
                  />
                </span>
              </label>
              <button
                uiButton
                [text]="true"
                [outlined]="false"
                *ngIf="elementFilter()"
                type="button"
                class="workflow-graph-view__clear-filter"
                aria-label="Clear element search"
                (click)="clearElementFilter()"
              >
                Clear
              </button>
            </div>
          </header>

          <div class="workflow-graph-view__picker-groups">
            <div
              *ngIf="workflowDecisionGateFinderItems().length > 0"
              class="workflow-graph-view__picker-group"
            >
              <h6>Workflow-wide decision gates</h6>
              <ul>
                <li
                  *ngFor="
                    let gate of workflowDecisionGateFinderItems();
                    trackBy: trackDecisionGate
                  "
                >
                  <div class="workflow-graph-view__picker-item-shell">
                    <button
                      uiButton
                      [wrap]="true"
                      severity="warn"
                      type="button"
                      class="workflow-graph-view__picker-gate-action"
                      [attr.aria-label]="decisionGateButtonAriaLabel(gate)"
                      [attr.aria-describedby]="
                        decisionGateButtonDescriptionId(gate)
                      "
                      (click)="activateDecisionGate(gate)"
                    >
                      <i class="pi pi-arrow-right" aria-hidden="true"></i>
                      {{ decisionGateButtonText(gate) }}
                    </button>
                    <span
                      class="visually-hidden"
                      [attr.id]="decisionGateButtonDescriptionId(gate)"
                    >
                      {{ decisionGateButtonDescription(gate) }}
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <div
              class="workflow-graph-view__picker-group"
              *ngFor="let group of selectionGroups()"
            >
              <h6>{{ group }}</h6>
              <ul>
                <li
                  *ngFor="
                    let item of itemsForGroup(group);
                    trackBy: trackElement
                  "
                >
                  <div class="workflow-graph-view__picker-item-shell">
                    <button
                      uiButton
                      [wrap]="true"
                      [text]="true"
                      [outlined]="false"
                      type="button"
                      [class.workflow-graph-view__picker-item--active]="
                        selectedElementId() === item.id
                      "
                      [attr.aria-label]="inspectAriaLabel(group, item)"
                      [attr.aria-describedby]="decisionGateDescriptionId(item)"
                      [attr.aria-pressed]="selectedElementId() === item.id"
                      (click)="selectItem(item, $event.detail === 0)"
                    >
                      <strong>{{ item.label }}</strong>
                      <span>{{ item.description }}</span>
                      <span
                        *ngIf="decisionGateSummary(item) as gateSummary"
                        class="workflow-graph-view__picker-gate-summary"
                      >
                        {{ gateSummary }}
                      </span>
                      <code>Stable ID · {{ item.sourceId || "(blank)" }}</code>
                    </button>

                    <span
                      *ngIf="
                        decisionGateFullDescription(item) as gateDescription
                      "
                      class="visually-hidden"
                      [attr.id]="decisionGateDescriptionId(item)"
                    >
                      {{ gateDescription }}
                    </span>

                    <button
                      uiButton
                      [wrap]="true"
                      severity="warn"
                      *ngFor="
                        let gate of decisionGatesForItem(item);
                        trackBy: trackDecisionGate
                      "
                      type="button"
                      class="workflow-graph-view__picker-gate-action"
                      [attr.aria-label]="decisionGateButtonAriaLabel(gate)"
                      [attr.aria-describedby]="
                        decisionGateButtonDescriptionId(gate)
                      "
                      (click)="activateDecisionGate(gate)"
                    >
                      <i class="pi pi-arrow-right" aria-hidden="true"></i>
                      {{ decisionGateButtonText(gate) }}
                    </button>
                    <span
                      *ngFor="
                        let gate of decisionGatesForItem(item);
                        trackBy: trackDecisionGate
                      "
                      class="visually-hidden"
                      [attr.id]="decisionGateButtonDescriptionId(gate)"
                    >
                      {{ decisionGateButtonDescription(gate) }}
                    </span>
                  </div>
                </li>
              </ul>
            </div>

            <p
              *ngIf="!hasFinderResults()"
              class="workflow-graph-view__no-results"
              role="status"
            >
              No workflow items match “{{ elementFilter().trim() }}”.
            </p>
          </div>
        </section>

        <section
          class="workflow-graph-view__canvas-region"
          [hidden]="contextualInspector && inspectorStandalone"
          [class.workflow-graph-view__canvas-region--workflow-requirements]="
            isOverviewMode() && hasWorkflowDecisionRequirements()
          "
          aria-label="Workflow canvas"
        >
          <div class="workflow-graph-view__toolbar">
            <div class="workflow-graph-view__toolbar-start">
              <nav
                *ngIf="contextualInspector"
                class="workflow-graph-view__surface-tabs"
                aria-label="Workflow overview display"
              >
                <button
                  uiButton
                  [text]="true"
                  [outlined]="false"
                  type="button"
                  [attr.aria-pressed]="!tableView()"
                  (click)="tableView.set(false)"
                >
                  Process map
                </button>
                <button
                  uiButton
                  [text]="true"
                  [outlined]="false"
                  type="button"
                  [attr.aria-pressed]="tableView()"
                  (click)="tableView.set(true)"
                >
                  Route table
                </button>
              </nav>
              <ng-content select="[workflowGraphToolbarActions]"></ng-content>
              <button
                uiButton
                *ngIf="contextualInspector"
                #elementFinderToggle
                type="button"
                class="workflow-graph-view__finder-toggle"
                aria-controls="workflow-graph-element-finder"
                [attr.aria-expanded]="elementFinderOpen()"
                (click)="toggleElementFinder()"
              >
                <i class="pi pi-search" aria-hidden="true"></i>
                Find elements
              </button>

              <div
                class="workflow-graph-view__legend"
                aria-label="Workflow diagram legend"
              >
                <span>
                  <i
                    class="workflow-graph-view__legend-route"
                    aria-hidden="true"
                  ></i>
                  Authored path
                </span>
                <span>
                  <i
                    class="workflow-graph-view__legend-requirement"
                    aria-hidden="true"
                  ></i>
                  Decision requirement
                </span>
                <span *ngIf="!isOverviewMode()">
                  <i
                    class="workflow-graph-view__legend-association"
                    aria-hidden="true"
                  ></i>
                  Applies to stage
                </span>
                <span
                  *ngIf="hasProcessSequence()"
                  title="Stage numbers are shown only for an unambiguous authored path sequence; they are not display order."
                >
                  <i
                    class="workflow-graph-view__legend-sequence"
                    aria-hidden="true"
                    >1</i
                  >
                  Path order
                  <span class="visually-hidden">
                    shown only for an unambiguous authored path; not display
                    order
                  </span>
                </span>
                <span
                  *ngIf="
                    currentGraph.invalidElementCount > 0 ||
                    workflowDecisionGateInvalidCount() > 0 ||
                    stepDecisionGateInvalidCount() > 0
                  "
                >
                  <i
                    class="workflow-graph-view__legend-invalid"
                    aria-hidden="true"
                  ></i>
                  Needs attention
                </span>
              </div>
            </div>

            <div class="workflow-graph-view__actions">
              <button uiButton *ngIf="canArrangeNodes" class="workflow-graph-view__text-action" type="button" [disabled]="isBusy()" (click)="autoArrange()"
                title="Restore the default layout for this draft">Auto arrange</button>
              <button uiButton *ngIf="contextualInspector && !inspectorStandalone" type="button" class="workflow-graph-view__text-action"
                [attr.aria-expanded]="!inspectorCollapsed()" (click)="toggleInspector()">{{inspectorCollapsed() ? 'Show inspector' : 'Hide inspector'}}</button>
              <button
                uiButton
                [wrap]="true"
                #diagramModeToggle
                type="button"
                class="workflow-graph-view__mode-toggle"
                [attr.aria-pressed]="!isOverviewMode()"
                [disabled]="isBusy()"
                (click)="toggleDiagramMode()"
                data-testid="workflow-graph-mode-toggle"
              >
                {{
                  isOverviewMode()
                    ? "Show responsibility view"
                    : "Show workflow view"
                }}
              </button>
              <button
                uiButton
                type="button"
                title="Zoom in"
                aria-label="Zoom in on workflow graph"
                [disabled]="!canControlVisual()"
                (click)="zoomIn()"
              >
                <i class="pi pi-search-plus" aria-hidden="true"></i>
              </button>
              <button
                uiButton
                type="button"
                title="Zoom out"
                aria-label="Zoom out of workflow graph"
                [disabled]="!canControlVisual()"
                (click)="zoomOut()"
              >
                <i class="pi pi-search-minus" aria-hidden="true"></i>
              </button>
              <button
                uiButton
                type="button"
                title="Fit graph"
                aria-label="Fit workflow graph to viewport"
                [disabled]="!canControlVisual()"
                (click)="fitGraph()"
              >
                <i class="pi pi-window-maximize" aria-hidden="true"></i>
              </button>
            </div>
          </div>

          <button
            uiButton
            [text]="true"
            [outlined]="false"
            *ngIf="isOverviewMode() && hasWorkflowDecisionRequirements()"
            type="button"
            class="workflow-graph-view__workflow-requirements"
            [class.workflow-graph-view__workflow-requirements--invalid]="
              workflowDecisionGateInvalidCount() > 0
            "
            [attr.aria-label]="
              'Open ' +
              workflowDecisionGateCount() +
              ' workflow-wide ' +
              (workflowDecisionGateCount() === 1
                ? 'requirement'
                : 'requirements') +
              (workflowDecisionGateInvalidCount() > 0
                ? ', needs attention'
                : '')
            "
            (click)="requestWorkflowDecisionScope()"
            data-testid="workflow-graph-workflow-requirements"
          >
            <span>
              {{ workflowDecisionGateCount() }} workflow-wide
              {{
                workflowDecisionGateCount() === 1
                  ? "requirement"
                  : "requirements"
              }}
            </span>
            <small *ngIf="workflowDecisionGateInvalidCount() > 0"
              >Needs attention</small
            >
            <i class="pi pi-arrow-right" aria-hidden="true"></i>
          </button>

          <div
            *ngIf="contextualInspector && tableView()"
            class="workflow-graph-view__route-table"
          >
            <table>
              <thead>
                <tr>
                  <th>From</th>
                  <th>Outcome / path</th>
                  <th>To</th>
                  <th><span class="visually-hidden">Inspect route</span></th>
                </tr>
              </thead>
              <tbody>
                <tr
                  *ngFor="let edge of currentGraph.edges"
                  [class.selected]="selectedElementId() === edge.id"
                >
                  <td>{{ edge.fromDisplayValue }}</td>
                  <td>
                    {{ edge.resultLabel || edge.label || "Continue"
                    }}<small *ngIf="edge.isInvalid">Needs attention</small>
                  </td>
                  <td>{{ edge.toDisplayValue }}</td>
                  <td>
                    <button
                      uiButton
                      type="button"
                      [attr.aria-label]="'Inspect route: ' + edge.description"
                      (click)="selectRouteFromTable(edge.id)"
                    >
                      Inspect
                    </button>
                  </td>
                </tr>
                <tr *ngIf="!currentGraph.edges.length">
                  <td colspan="4">No saved routes.</td>
                </tr>
              </tbody>
            </table>
          </div>
          <div
            [hidden]="contextualInspector && tableView()"
            class="workflow-graph-view__canvas-wrap"
            [class.workflow-graph-view__canvas-wrap--stale]="isVisualStale()"
            [attr.aria-busy]="isBusy() ? 'true' : null"
          >
            <div
              #graphContainer
              class="workflow-graph-view__canvas"
              role="img"
              [attr.aria-label]="canvasAriaLabel() + (canArrangeNodes ? '. Drag nodes to arrange the map. Select a node and use arrow keys to move it; Shift moves farther. Use the left step list to open an editor.' : '')"
              [attr.tabindex]="canArrangeNodes ? 0 : null"
              (keydown)="onMapKeydown($event)"
            ></div>
            <div
              *ngIf="isBusy()"
              class="workflow-graph-view__overlay"
              aria-hidden="true"
            >
              {{
                isWaitingForViewport()
                  ? "Preparing graph"
                  : hasRenderedVisual()
                    ? "Updating graph"
                    : "Rendering graph"
              }}
            </div>
          </div>
        </section>

        <div *ngIf="contextualInspector && !inspectorStandalone" [hidden]="inspectorCollapsed()" class="workflow-graph-view__resize-handle" role="separator"
          tabindex="0" aria-orientation="vertical" aria-label="Resize designer inspector" aria-valuemin="260" aria-valuemax="640"
          [attr.aria-valuenow]="inspectorWidth()" title="Drag to resize inspector; arrow keys adjust width"
          (pointerdown)="startInspectorResize($event)" (pointermove)="resizeInspector($event)"
          (pointerup)="endInspectorResize($event)" (pointercancel)="endInspectorResize($event)"
          (keydown)="onInspectorResizeKeydown($event)"></div>
        <aside
          class="workflow-graph-view__inspector"
          [hidden]="contextualInspector && !inspectorStandalone && inspectorCollapsed()"
          [class.workflow-graph-view__inspector--contextual]="
            contextualInspector
          "
          [attr.aria-label]="
            contextualInspector
              ? contextualInspectorAriaLabel()
              : 'Selected workflow graph item'
          "
          [attr.aria-live]="contextualInspector ? null : 'polite'"
        >
          <div
            class="workflow-graph-view__projected-inspector"
            [hidden]="!contextualInspector"
          >
            <ng-content select="[workflowGraphInspector]"></ng-content>
          </div>

          <div
            class="workflow-graph-view__static-inspector"
            [hidden]="contextualInspector"
          >
            <strong class="workflow-graph-view__inspector-label"
              >Inspector</strong
            >
            <ng-container
              *ngIf="selectedElement() as item; else emptySelection"
            >
              <span class="workflow-graph-view__inspector-type">{{
                item.detail.subtitle
              }}</span>
              <h5>{{ item.detail.title }}</h5>
              <p>{{ item.detail.description }}</p>
              <p
                *ngIf="decisionGateSummary(item) as gateSummary"
                class="workflow-graph-view__inspector-gate-summary"
              >
                <strong>Decision gates</strong>
                {{ gateSummary }}
              </p>
              <dl>
                <div *ngFor="let fact of item.detail.facts">
                  <dt>{{ fact.label }}</dt>
                  <dd>{{ fact.value }}</dd>
                </div>
              </dl>

              <button
                uiButton
                [wrap]="true"
                severity="primary"
                [outlined]="false"
                *ngIf="selectedContextualAction() as action"
                type="button"
                class="workflow-graph-view__context-action"
                (click)="requestContextualEdit(action)"
              >
                {{ action.label }}
                <i class="pi pi-arrow-right" aria-hidden="true"></i>
              </button>
            </ng-container>

            <ng-template #emptySelection>
              <h5>Nothing selected</h5>
              <p>
                {{ emptyInspectorMessage() }}
              </p>
            </ng-template>
          </div>
        </aside>
      </div>
    </ng-container>
  </ng-container>
</section>
